// ============================================================================
// JARVISTRAVEL - OBSERVABILITY
// Central error-reporting seam. Every unhandled rejection, boundary crash, and
// (optionally) surfaced API failure routes through here, so wiring a real
// service (Sentry, etc.) in production is a change to THIS one file — not a
// codebase-wide hunt. Today it logs structured records to the console and never
// swallows anything.
// ============================================================================

export type Severity = 'error' | 'warning' | 'info';
export type CaptureContext = Record<string, unknown>;

function toError(value: unknown): Error {
  if (value instanceof Error) return value;
  if (typeof value === 'string') return new Error(value);
  try {
    return new Error(JSON.stringify(value));
  } catch {
    return new Error(String(value));
  }
}

/**
 * Call once at startup (main.tsx). The place to initialise a telemetry SDK.
 * Intentionally a no-op until a provider is chosen — every capture* below
 * already funnels reports through this module, so wiring stays local.
 */
export function initObservability(): void {
  // e.g. Sentry.init({ dsn: import.meta.env.VITE_SENTRY_DSN, environment: import.meta.env.MODE });
}

/** Report a caught/uncaught error. Coerces non-Errors so we always have a stack. */
export function captureException(error: unknown, context?: CaptureContext): void {
  const err = toError(error);
  // e.g. Sentry.captureException(err, { extra: context });
  console.error('[capture]', err.message, { ...context, stack: err.stack });
}

/** Report a noteworthy non-exception event (e.g. a degraded-but-handled state). */
export function captureMessage(message: string, severity: Severity = 'info', context?: CaptureContext): void {
  // e.g. Sentry.captureMessage(message, { level: severity, extra: context });
  const log = severity === 'error' ? console.error : severity === 'warning' ? console.warn : console.info;
  log('[capture]', message, context ?? {});
}


// ---------------------------------------------------------------------------
// warnOnce — session-deduped console noise control (JAR-292)
// API-fallback notices fire every poll cycle; unchecked they bury real errors
// (76+ identical lines in minutes). Log each key once per session, with error
// details serialized so the console never shows "[object Object]".
// ---------------------------------------------------------------------------

const warnedKeys = new Set<string>();

// A console line is for a developer, and it is bounded. Past this a detail is
// cut and its full length named, so one oversized object cannot fill the
// console and a response body cannot arrive in it whole (JAR-1536).
const DETAIL_MAX = 500;

// Every detail goes through this, whatever its type (web-app#379 review). The
// first version bounded only objects, so a raw string (a response body passed
// straight in) and a long Error message were still logged whole.
function bounded(text: string): string {
  return text.length > DETAIL_MAX ? `${text.slice(0, DETAIL_MAX)}... (${text.length} chars)` : text;
}

function serializeDetail(detail: unknown): unknown {
  if (detail instanceof Error) return bounded(detail.message);
  if (typeof detail === 'string') return bounded(detail);
  if (detail && typeof detail === 'object') {
    try {
      // An Error NESTED in an object used to vanish. Its message is not an own
      // enumerable property, so JSON.stringify renders it `{}`, and a caller
      // passing `{ query, error }` logged the query and lost the error. Mapping
      // every Error to its message fixes that, and it keeps the rest of an
      // Error, such as an ApiRequestError's rawBody excerpt, out of the console.
      const json = JSON.stringify(detail, (_key, value: unknown) => (value instanceof Error ? value.message : value));
      return bounded(json);
    } catch {
      return String(detail);
    }
  }
  return detail;
}

// Callers currently pass fixed literals, but a future dynamic key (one per
// trip id, say) must not grow the set unbounded for the life of the session.
const WARNED_KEYS_MAX = 200;

/**
 * console.warn once per session for a given failure; extra detail is serialized.
 *
 * "A given failure" is the key plus, when the first detail is an Error, that
 * Error's message as the console prints it, bounded (JAR-1536). Bounded so the
 * dedupe memory is as bounded as the output (web-app#379 review): the raw
 * message made each entry as large as the payload DETAIL_MAX exists to cut.
 * Keyed on the key alone, a later failure of a DIFFERENT kind under the same key
 * logged nothing, so the console showed the first cause and hid the one you
 * were debugging. The repeat that JAR-292 was about is the same failure every
 * poll, and that still logs once. Detail that is not an Error does not split
 * the key: it often varies per call (a query, an id), and splitting on it would
 * bring the noise back.
 */
export function warnOnce(key: string, ...detail: unknown[]): void {
  const first = detail[0];
  const signature = first instanceof Error ? `${key}\u0000${bounded(first.message)}` : key;
  if (warnedKeys.has(signature)) return;
  if (warnedKeys.size >= WARNED_KEYS_MAX) warnedKeys.clear();
  warnedKeys.add(signature);
  console.warn(key, ...detail.map(serializeDetail));
}

/** Test hook — clears the once-per-session memory. */
export function resetWarnOnce(): void {
  warnedKeys.clear();
}
